import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { TableActionBar, TableCell, TableHead } from 'common/components/Table';
import { FilterBar } from 'common/components/FilterBar';
import { Loader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { Checkbox } from 'common/components/Checkbox';
import { IconButton } from 'common/components/IconButton';
import { Tooltip } from 'common/components/Tooltip';
import {
    IPaginationProps,
    ITableSelectionProps,
    TTableAction,
} from 'common/types';
import { ITrainingGroupListItemFragment } from 'graphql/types';
import { TrainingGroupTableRow } from 'training/components/TrainingGroupTable/TrainingGroupTableRow';

interface IProps {
    groups: ITrainingGroupListItemFragment[];
    isEditable?: boolean;
    loading?: boolean;
    paginationProps: IPaginationProps;
    searchValue: string;
    selectionProps?: ITableSelectionProps<ITrainingGroupListItemFragment>;
    tableActions?: TTableAction[];
    trainingId: string;
    onSearch(searchValue: string): void;
}

export const TrainingGroupTable = ({
    groups,
    isEditable,
    loading,
    paginationProps,
    searchValue,
    selectionProps,
    tableActions,
    trainingId,
    onSearch,
}: IProps) => {
    const [translate] = useTranslation();

    const { paginationSettings, setPage } = paginationProps;
    const {
        rows,
        isSelected,
        selected,
        selectableItems,
        setSelected,
        onSelectAll,
    } = selectionProps || {};

    const handleSearch = (value: string) => {
        setPage(1);
        onSearch(value);

        if (selected?.length) setSelected?.([]);
    };

    const actions = tableActions?.map(
        ({ tooltipTitle, iconPath, onClick }, index) => (
            <Tooltip key={index} title={tooltipTitle}>
                <Box>
                    <IconButton
                        color="inherit"
                        disabled={!isSelected}
                        iconPath={iconPath}
                        iconSize="2.4rem"
                        onClick={() => onClick(selected || [])}
                    />
                </Box>
            </Tooltip>
        )
    );

    return (
        <>
            <FilterBar
                useFilterDrawer
                filterDrawerProps={{
                    title: translate('filterForm.trainingGroups.title'),
                    description: translate(
                        'filterForm.trainingGroups.description'
                    ),
                }}
                initialSearchValue={searchValue}
                placeholder={translate('filterBarPlaceholder')}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!groups.length && (
                <TableContainer>
                    {isEditable && selectionProps && (
                        <TableActionBar
                            actions={actions}
                            selectionProps={selectionProps}
                        />
                    )}
                    <Table>
                        <TableHead>
                            <TableRow>
                                {isEditable && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={false}
                                            disabled={
                                                isSelected ||
                                                !selectableItems?.length ||
                                                loading
                                            }
                                            onChange={onSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>{translate('name')}</TableCell>
                                <TableCell>
                                    {translate('trainingGroups.userCount')}
                                </TableCell>
                                <TableCell>
                                    {translate('trainingGroups.progress')}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows?.length
                                ? rows.map(
                                      ({ index, onSelect, isSelected }) => {
                                          const group = groups[index];

                                          if (!group) return null;

                                          return (
                                              <TrainingGroupTableRow
                                                  group={group}
                                                  isSelectable={isEditable}
                                                  isSelected={isSelected}
                                                  key={group.id}
                                                  trainingId={trainingId}
                                                  onSelect={onSelect}
                                              />
                                          );
                                      }
                                  )
                                : groups.map((group) => (
                                      <TrainingGroupTableRow
                                          group={group}
                                          key={group.id}
                                          trainingId={trainingId}
                                      />
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && (
                <Box sx={{ position: 'relative', height: '100px', mt: 4 }}>
                    <Loader />
                </Box>
            )}

            {!loading && !groups.length && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography>{translate('noOptionsText.groups')}</Typography>
                </Box>
            )}

            {!loading && !!paginationSettings && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate('trainingGroups.group', {
                            count: paginationSettings.count,
                        })}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}
        </>
    );
};
